import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { formatDateTime } from '../../../../config/appConfig';
import { useClient360Timeline } from './useClient360Timeline';
import type { TimelineEvent, TimelineFilter, TimelineEventType } from './types';

interface ClientActivityTimelineProps {
  clientId: number;
}

const FILTER_OPTIONS: { value: TimelineFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'email', label: 'Emails' },
  { value: 'appointment', label: 'Appointments' },
  { value: 'todo', label: 'Todos' },
  { value: 'activity', label: 'Activity' },
];

const TYPE_META: Record<
  TimelineEventType,
  { label: string; dotClass: string; icon: React.ReactNode }
> = {
  email: {
    label: 'Email',
    dotClass: 'bg-amber-500',
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
  },
  appointment: {
    label: 'Appointment',
    dotClass: 'bg-indigo-500',
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
  },
  todo: {
    label: 'Todo',
    dotClass: 'bg-emerald-500',
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
  },
  activity: {
    label: 'Activity',
    dotClass: 'bg-slate-500',
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
};

function TimelineItem({ event }: { event: TimelineEvent }) {
  const { colors } = useTheme();
  const meta = TYPE_META[event.type];
  const dateLabel = event.date ? formatDateTime(event.date) : '—';
  const completed = event.meta?.completed;

  const content = (
    <div className="flex gap-3 min-w-0">
      <div className="flex flex-col items-center shrink-0 pt-1">
        <span className={cn('w-2.5 h-2.5 rounded-full ring-4 ring-offset-0', meta.dotClass, 'ring-black/5 dark:ring-white/10')} />
        <span className="w-px flex-1 bg-current opacity-10 mt-1 min-h-[1rem]" aria-hidden />
      </div>
      <div className="flex-1 min-w-0 pb-4">
        <div className="flex flex-wrap items-center gap-2 mb-0.5">
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
              colors.surfaceHover,
              colors.textSecondary,
            )}
          >
            {meta.icon}
            {meta.label}
          </span>
          <time className={cn('text-xs tabular-nums', colors.textSecondary)} dateTime={event.date}>
            {dateLabel}
          </time>
        </div>
        <p
          className={cn(
            'text-sm font-medium',
            colors.text,
            completed && 'line-through opacity-60',
          )}
        >
          {event.title}
        </p>
        {event.subtitle ? (
          <p className={cn('text-xs mt-0.5 truncate', colors.textSecondary)}>{event.subtitle}</p>
        ) : null}
      </div>
    </div>
  );

  if (event.link) {
    return (
      <Link to={event.link} className="block rounded-lg -mx-2 px-2 hover:bg-black/[0.03] dark:hover:bg-white/[0.03] transition-colors">
        {content}
      </Link>
    );
  }

  return content;
}

const ClientActivityTimeline: React.FC<ClientActivityTimelineProps> = ({ clientId }) => {
  const { colors } = useTheme();
  const [filter, setFilter] = useState<TimelineFilter>('all');
  const { events, isLoading, isError } = useClient360Timeline(clientId);

  const filtered = useMemo(() => {
    if (filter === 'all') return events;
    return events.filter((e) => e.type === filter);
  }, [events, filter]);

  const counts = useMemo(() => {
    const c: Record<TimelineFilter, number> = { all: events.length, email: 0, appointment: 0, todo: 0, activity: 0 };
    for (const e of events) c[e.type] += 1;
    return c;
  }, [events]);

  return (
    <div className={cn('rounded-xl border overflow-hidden', colors.border, colors.background)}>
      <div className={cn('px-4 py-3 border-b', colors.border, colors.surfaceHover)}>
        <h3 className={cn('text-sm font-semibold', colors.text)}>Activity timeline</h3>
        <p className={cn('text-xs mt-0.5', colors.textSecondary)}>
          Emails, appointments, todos, and audit log in one place.
        </p>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {FILTER_OPTIONS.map((opt) => {
            const count = counts[opt.value];
            const active = filter === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFilter(opt.value)}
                className={cn(
                  'rounded-lg px-2.5 py-1 text-xs font-medium transition-colors',
                  active
                    ? 'bg-blue-600 text-white dark:bg-blue-500'
                    : cn(colors.surfaceHover, colors.textSecondary, 'hover:opacity-90'),
                )}
              >
                {opt.label}
                {count > 0 ? ` (${count})` : ''}
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 max-h-[420px] overflow-y-auto">
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className={cn('h-14 rounded-lg animate-pulse', colors.surfaceHover)} />
            ))}
          </div>
        ) : isError ? (
          <p className={cn('text-sm text-red-600 dark:text-red-400')}>Could not load timeline.</p>
        ) : filtered.length === 0 ? (
          <p className={cn('text-sm', colors.textSecondary)}>No activity yet for this filter.</p>
        ) : (
          <div>
            {filtered.map((event) => (
              <TimelineItem key={event.id} event={event} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientActivityTimeline;
